/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { breakpoints } from '@coursera/cds-core';

import type { ConsumerPageHeader_EnterpriseProgramFragment as EnterpriseProgramFragment } from 'bundles/composable-page-header-consumer/queries/__generated__/ConsumerMyPageHeaderQuery';
import GrowthDiscoveryEpicClient from 'bundles/epic/clients/GrowthDiscovery';
import EnterpriseSearchBar from 'bundles/search-common/components/searchbar/enterprise/SearchBar';

import _t from 'i18n!nls/composable-page-header-enterprise';

type Props = {
  thirdPartyOrganizationSlug?: string;
  enrolledEnterprisePrograms?: Partial<EnterpriseProgramFragment>[] | null;
};

const styles = {
  searchBar: css`
    width: 100%;
    max-width: 480px;
    min-width: 0;

    ${breakpoints.up('lg')} {
      width: 100%;
    }
  `,
};

export function EnterpriseOrgScopedSearchBarGridItem(props: Props) {
  const { thirdPartyOrganizationSlug, enrolledEnterprisePrograms } = props;

  if (!thirdPartyOrganizationSlug || !enrolledEnterprisePrograms?.length) {
    return null;
  }

  const orgPrograms = enrolledEnterprisePrograms?.filter(
    (program) => program?.naptimeThirdPartyOrganization?.slug === thirdPartyOrganizationSlug
  );

  const programIds: Array<string> = orgPrograms?.map((program) => program?.id ?? '') ?? [];
  const primaryProgramSlug = orgPrograms?.[0]?.slug;

  if (!primaryProgramSlug) {
    return null;
  }

  const orgName = orgPrograms?.[0]?.naptimeThirdPartyOrganization?.name;

  // EPIC users are redirected to the org-scoped search page; Statsig-only users stay on the program browse URL.
  const isOrgScopedHomeEnabled = GrowthDiscoveryEpicClient.preview('orgScopedHome');
  const searchPath = isOrgScopedHomeEnabled
    ? `/organizations/${thirdPartyOrganizationSlug}/search`
    : `/programs/${primaryProgramSlug}/browse`;

  return (
    <div data-testid="org-scoped-enterprise-search-bar" css={styles.searchBar}>
      <EnterpriseSearchBar
        enterpriseSearchBarConfig={{
          programIds,
          pathname: searchPath,
          searchResultsBasePath: searchPath,
          placeholder: orgName
            ? _t('Search #{orgName} learning on Coursera', { orgName })
            : _t('Search learning on Coursera'),
          ...(isOrgScopedHomeEnabled
            ? { enterpriseOrgSlug: thirdPartyOrganizationSlug }
            : { enterpriseProgramSlug: primaryProgramSlug }),
        }}
      />
    </div>
  );
}

export default EnterpriseOrgScopedSearchBarGridItem;
